// @djsem/ui — shared primitives. APIs are stable (Badge, PanelShell unchanged);
// visuals refined to the champagne-gold token set. New: Button, StatChip, ApiResourceView,
// TextField, PillToggleGroup, CheckboxRow (see docs/REACT_COMPONENT_PLAYBOOK.md).

import type { ReactNode, CSSProperties } from "react";
import { tokens, camelotColor } from "./tokens";
import { LoadingState, EmptyState, ErrorState, OfflineState, PermissionDeniedState } from "./states";
import type { UseApiResource } from "./apiHooks";

/**
 * Small rounded status label. `tone` picks the accent color (neutral/accent/ok/warn).
 * @param children - Badge content.
 * @param tone - Visual tone; defaults to `"neutral"`.
 */
export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "accent" | "ok" | "warn" }) {
  const map: Record<string, CSSProperties> = {
    neutral: { color: tokens.color.muted, border: `1px solid ${tokens.color.border}`, background: "transparent" },
    accent: { color: tokens.color.accent, border: `1px solid ${tokens.color.accentDim}`, background: "rgba(201,163,90,0.06)" },
    ok: { color: tokens.color.ok, border: `1px solid ${tokens.color.okDim}`, background: "rgba(127,206,158,0.05)" },
    warn: { color: tokens.color.danger, border: `1px solid ${tokens.color.dangerDim}`, background: "rgba(224,138,138,0.05)" },
  };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 9px", borderRadius: tokens.radius.sm,
      fontSize: tokens.text.xs, fontWeight: 500, letterSpacing: "0.03em",
      whiteSpace: "nowrap", ...map[tone],
    }}>{children}</span>
  );
}

/**
 * Standard card/panel surface with an optional uppercase title header.
 * @param title - Optional panel heading.
 * @param children - Panel content.
 * @param style - Optional style overrides merged onto the panel.
 */
export function PanelShell({ title, children, style }: { title?: string; children: ReactNode; style?: CSSProperties }) {
  return (
    <section className="djsem-panel" style={{
      background: `linear-gradient(180deg, ${tokens.color.surface} 0%, ${tokens.color.surface2} 100%)`,
      border: `1px solid ${tokens.color.border}`,
      borderRadius: tokens.radius.md,
      boxShadow: tokens.shadow.panel,
      padding: tokens.space(4),
      color: tokens.color.text,
      fontFamily: tokens.font.sans,
      ...style,
    }}>
      {title ? (
        <h3 style={{
          margin: "0 0 10px", fontSize: tokens.text.xs, fontWeight: 600,
          letterSpacing: "0.12em", textTransform: "uppercase", color: tokens.color.muted,
          paddingBottom: 8, borderBottom: `1px solid ${tokens.color.border}`,
        }}>{title}</h3>
      ) : null}
      {children}
    </section>
  );
}

/**
 * Standard button. `variant="primary"` = gold fill for the one main action per view.
 * @param children - Button label/content.
 * @param onClick - Click handler.
 * @param variant - `"default"` or `"primary"`; defaults to `"default"`.
 * @param disabled - Disables the button when true.
 * @param title - Optional tooltip text.
 */
export function Button({ children, onClick, variant = "default", disabled, title }: {
  children: ReactNode; onClick?: () => void; variant?: "default" | "primary"; disabled?: boolean; title?: string;
}) {
  return (
    <button
      className={`djsem-btn${variant === "primary" ? " djsem-btn--primary" : ""}`}
      onClick={onClick} disabled={disabled} title={title}
    >{children}</button>
  );
}

/**
 * Compact figure + label — for dashboard stats rows.
 * @param label - Small caption under the value.
 * @param value - The stat's headline figure.
 * @param tone - Value color tone; defaults to `"neutral"`.
 */
export function StatChip({ label, value, tone = "neutral" }: {
  label: string; value: ReactNode; tone?: "neutral" | "accent" | "ok" | "warn";
}) {
  const valueColor = tone === "accent" ? tokens.color.accent : tone === "ok" ? tokens.color.ok : tone === "warn" ? tokens.color.danger : tokens.color.text;
  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 2,
      padding: "10px 14px", minWidth: 96,
      background: tokens.color.bg, border: `1px solid ${tokens.color.border}`, borderRadius: tokens.radius.md,
    }}>
      <span style={{ fontSize: 22, fontWeight: 600, color: valueColor, fontFamily: tokens.font.sans, letterSpacing: "-0.01em" }}>{value}</span>
      <span style={{ fontSize: tokens.text.xs, color: tokens.color.muted, letterSpacing: "0.05em", textTransform: "uppercase" }}>{label}</span>
    </div>
  );
}

/**
 * Collapses the canonical loading/offline/permission_denied/error/empty ternary chain (prompt
 * 89's required states) into one component. Every screen that lists an API resource renders
 * through this instead of hand-copying the 5-branch chain — see docs/REACT_COMPONENT_PLAYBOOK.md.
 * @param resource - The {@link UseApiResource} to render (from `useApiResource`).
 * @param empty - Optional empty-state title/detail override.
 * @param errorTitle - Optional error-state title override.
 * @param retryLabel - Optional retry-button label override.
 * @param render - Renders the loaded data when the resource is ready.
 */
export function ApiResourceView<T>({ resource, empty, errorTitle, retryLabel, render }: {
  resource: UseApiResource<T>;
  empty?: { title: string; detail?: ReactNode };
  errorTitle?: string;
  retryLabel?: string;
  render: (data: T) => ReactNode;
}) {
  const { view, refetch } = resource;
  switch (view.state) {
    case "loading": return <LoadingState />;
    case "offline": return <OfflineState />;
    case "permission_denied": return <PermissionDeniedState />;
    case "error": return <ErrorState title={errorTitle} detail={view.message} retry={<button onClick={refetch}>{retryLabel ?? "Retry"}</button>} />;
    case "empty": return <EmptyState title={empty?.title ?? "Nothing here yet"} detail={empty?.detail} />;
    default: return view.data !== undefined ? <>{render(view.data)}</> : null;
  }
}

/**
 * Standard text input — the repeated `<input style={{padding:8,...}}>` block, one place.
 * @param value - Current input value.
 * @param onChange - Called with the new value on every change.
 * @param placeholder - Optional placeholder text.
 * @param type - Input type; defaults to `"text"`.
 * @param style - Optional style overrides merged onto the input.
 */
export function TextField({ value, onChange, placeholder, type = "text", style }: {
  value: string; onChange: (value: string) => void; placeholder?: string; type?: string; style?: CSSProperties;
}) {
  return (
    <input
      type={type} placeholder={placeholder} value={value}
      onChange={(e: { target: { value: string } }) => onChange(e.target.value)}
      style={{ padding: 8, fontSize: 12, background: tokens.color.bg, color: tokens.color.text, border: `1px solid ${tokens.color.border}`, borderRadius: 4, ...style }}
    />
  );
}

/**
 * A row of mutually-exclusive pill buttons — source-platform pickers, mode pickers, output-type
 * pickers all use this shape.
 * @param options - The selectable values, rendered in order.
 * @param value - The currently-selected option.
 * @param onChange - Called with the newly-selected option.
 * @param labelFor - Optional label override per option; defaults to the raw value.
 * @param titleFor - Optional tooltip text per option.
 */
export function PillToggleGroup<T extends string>({ options, value, onChange, labelFor, titleFor }: {
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
  labelFor?: (option: T) => string;
  titleFor?: (option: T) => string | undefined;
}) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {options.map((opt) => (
        <button key={opt} onClick={() => onChange(opt)} title={titleFor?.(opt)} style={{
          // Task #22: actually pill-shaped now (radius.full) — this component was named
          // "Pill" since it was first written but used a 4px rect radius until this pass.
          fontSize: 11, padding: "5px 12px", borderRadius: tokens.radius.full, cursor: "pointer",
          background: value === opt ? tokens.color.accent : tokens.color.surface2,
          color: value === opt ? "#12100a" : tokens.color.text,
          border: `1px solid ${value === opt ? tokens.color.accentDim : tokens.color.border}`,
        }}>{labelFor ? labelFor(opt) : opt}</button>
      ))}
    </div>
  );
}

/**
 * The repeated `<label><input type="checkbox"/> text</label>` rights/credit-confirmation row.
 * @param checked - Current checked state.
 * @param onChange - Called with the new checked state.
 * @param children - Label content shown beside the checkbox.
 */
export function CheckboxRow({ checked, onChange, children }: {
  checked: boolean; onChange: (checked: boolean) => void; children: ReactNode;
}) {
  return (
    <label style={{ display: "block", fontSize: 12, marginTop: 4 }}>
      <input type="checkbox" checked={checked} onChange={(e: { target: { checked: boolean } }) => onChange(e.target.checked)} />
      {" "}{children}
    </label>
  );
}

// ---- Task #22: Djoid-inspired track metadata chips ----
// Djoid's library/set-builder screens attach small colored chips to every track (Camelot key,
// BPM, energy, danceability) instead of plain text — this is the one piece of its visual
// language DJSEM had the data model for (DjoidTrackRow's bpm/energy/camelot fields) but never
// actually rendered anywhere (confirmed: SetsModule's demo tracks only fed aggregate risk
// scoring, never shown per-track).

/** A single colored key pill — the wheel color comes from the Camelot position, not a fixed tone. */
export function KeyChip({ camelot }: { camelot: string }) {
  const color = camelotColor(camelot);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "1px 8px", borderRadius: tokens.radius.full,
      fontSize: tokens.text.xs, fontWeight: 600, letterSpacing: "0.02em",
      color: "#0a0b0d", background: color,
    }}>{camelot}</span>
  );
}

/** Compact energy readout — a row of filled/unfilled dots (0-1 scale), Djoid's own convention for showing energy at a glance without a numeric label. */
export function EnergyDots({ energy, count = 5 }: { energy: number; count?: number }) {
  const filled = Math.round(Math.max(0, Math.min(1, energy)) * count);
  return (
    <span style={{ display: "inline-flex", gap: 2 }} title={`Energy ${Math.round(energy * 100)}%`}>
      {Array.from({ length: count }, (_, i) => (
        <span key={i} style={{
          width: 5, height: 5, borderRadius: "50%",
          background: i < filled ? tokens.color.accent : tokens.color.border,
        }} />
      ))}
    </span>
  );
}

/** The full inline chip row for a track: key, BPM, energy — the compact per-track summary Djoid shows on every card/row. */
export function TrackMetaChips({ bpm, energy, camelot }: { bpm?: number; energy?: number; camelot?: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      {camelot ? <KeyChip camelot={camelot} /> : null}
      {bpm !== undefined ? <span style={{ fontSize: tokens.text.xs, color: tokens.color.textDim }}>{bpm} BPM</span> : null}
      {energy !== undefined ? <EnergyDots energy={energy} /> : null}
    </span>
  );
}
